/**
 * Network Graph Generator
 * Generates network graph SVG for team report
 *
 * Requirements 25-32:
 * - Calculate similarity between members using 2x2 matrix representation
 * - Apply dominant element correction
 * - Generate network graph with radar charts as nodes
 * - Connect similar members with edges (top 50% only)
 */

import type { MemberScore } from './teamReportGenerator';

/**
 * Edge between two members with similarity score
 */
export interface NetworkEdge {
  source: number;
  target: number;
  similarity: number;
  tier: 1 | 2; // 1 = top 25% (thick), 2 = 25-50% (thin)
}

/**
 * Node position for network layout
 */
export interface NetworkNode {
  id: number;
  name: string;
  scores: { A: number; B: number; C: number; D: number };
  x: number;
  y: number;
}

/**
 * Network graph data
 */
export interface NetworkGraphData {
  nodes: NetworkNode[];
  edges: NetworkEdge[];
}

/**
 * Convert ABCD scores to 2x2 matrix representation
 * A=x11, D=x12, B=x21, C=x22
 */
function scoresToMatrix(scores: { A: number; B: number; C: number; D: number }): number[] {
  return [scores.A, scores.D, scores.B, scores.C]; // [x11, x12, x21, x22]
}

/**
 * Calculate Frobenius norm distance between two matrices
 * sqrt(sum((a[i] - b[i])^2))
 */
function frobeniusDistance(matrixA: number[], matrixB: number[]): number {
  let sumSquares = 0;
  for (let i = 0; i < matrixA.length; i++) {
    sumSquares += Math.pow(matrixA[i] - matrixB[i], 2);
  }
  return Math.sqrt(sumSquares);
}

/**
 * Determine dominant elements based on score distribution
 *
 * Rules:
 * - 1 element significantly larger → that 1 element
 * - 2 elements dominate → those 2 elements
 * - 1 element significantly smaller → remaining 3 elements
 * - All 4 roughly equal → all 4 elements
 */
function getDominantElements(scores: { A: number; B: number; C: number; D: number }): Set<string> {
  const elements = [
    { key: 'A', value: scores.A },
    { key: 'B', value: scores.B },
    { key: 'C', value: scores.C },
    { key: 'D', value: scores.D },
  ];

  const total = elements.reduce((sum, e) => sum + e.value, 0);
  if (total === 0) return new Set(['A', 'B', 'C', 'D']);

  // Calculate ratios
  const ratios = elements.map((e) => ({ ...e, ratio: e.value / total }));

  // Sort by value descending
  const sorted = [...ratios].sort((a, b) => b.value - a.value);

  // Calculate mean and standard deviation
  const mean = total / 4;
  const variance = elements.reduce((sum, e) => sum + Math.pow(e.value - mean, 2), 0) / 4;
  const stdDev = Math.sqrt(variance);

  // Coefficient of variation (CV) to measure uniformity
  const cv = mean > 0 ? stdDev / mean : 0;

  // If CV is very low (< 0.15), all elements are roughly equal
  if (cv < 0.15) {
    return new Set(['A', 'B', 'C', 'D']);
  }

  // Check if one element is significantly larger (> mean + 1.5*stdDev)
  const highThreshold = mean + 1.5 * stdDev;
  const lowThreshold = mean - 1.5 * stdDev;

  const highElements = elements.filter((e) => e.value > highThreshold);
  const lowElements = elements.filter((e) => e.value < lowThreshold);

  // One element significantly larger
  if (highElements.length === 1 && lowElements.length === 0) {
    return new Set([highElements[0].key]);
  }

  // Two elements dominate (top 2 are significantly larger)
  if (sorted[0].value + sorted[1].value > total * 0.65) {
    return new Set([sorted[0].key, sorted[1].key]);
  }

  // One element significantly smaller
  if (lowElements.length === 1) {
    const dominant = elements.filter((e) => e.key !== lowElements[0].key);
    return new Set(dominant.map((e) => e.key));
  }

  // Default: top 2 elements
  return new Set([sorted[0].key, sorted[1].key]);
}

/**
 * Calculate dominant element overlap between two members
 * Returns 0-4 (number of matching dominant elements)
 */
function calculateDominantOverlap(
  scoresA: { A: number; B: number; C: number; D: number },
  scoresB: { A: number; B: number; C: number; D: number }
): number {
  const dominantA = getDominantElements(scoresA);
  const dominantB = getDominantElements(scoresB);

  let overlap = 0;
  for (const elem of dominantA) {
    if (dominantB.has(elem)) {
      overlap++;
    }
  }

  return overlap;
}

/**
 * Calculate similarity between two members
 * Combines matrix distance with dominant element correction
 *
 * @param scoresA First member's scores
 * @param scoresB Second member's scores
 * @returns Similarity value (0-1, higher = more similar)
 */
export function calculateMemberSimilarity(
  scoresA: { A: number; B: number; C: number; D: number },
  scoresB: { A: number; B: number; C: number; D: number }
): number {
  // Convert to matrix representation
  const matrixA = scoresToMatrix(scoresA);
  const matrixB = scoresToMatrix(scoresB);

  // Calculate base distance (Frobenius norm)
  // Max possible distance is sqrt(4 * 12^2) = 24 (when one is all 0, other is all 12)
  const distance = frobeniusDistance(matrixA, matrixB);
  const maxDistance = 24;

  // Convert distance to similarity (0-1)
  const baseSimilarity = 1 - distance / maxDistance;

  // Apply dominant element correction
  // Overlap of 0-4 dominant elements
  const overlap = calculateDominantOverlap(scoresA, scoresB);

  // Correction factor: each matching dominant element reduces distance by ~5%
  // overlap 0 → no change, overlap 4 → +20% similarity boost
  const correctionFactor = 1 + overlap * 0.05;

  // Apply correction and clamp to [0, 1]
  const adjustedSimilarity = Math.min(1, baseSimilarity * correctionFactor);

  return adjustedSimilarity;
}

/**
 * Network graph options
 */
export interface NetworkGraphOptions {
  edgeTopPercent?: number; // Top X% of edges to consider (default: 10)
  similarityThreshold?: number; // Minimum similarity threshold 0-1 (default: 0.7)
}

/**
 * Calculate all pairwise similarities and filter based on options
 * @param members Array of member scores
 * @param edgeTopPercent Top X% of edges to consider (default: 10)
 * @param similarityThreshold Minimum similarity threshold 0-1 (default: 0.7)
 */
export function calculateNetworkEdges(
  members: MemberScore[],
  edgeTopPercent: number = 10,
  similarityThreshold: number = 0.7
): NetworkEdge[] {
  const allEdges: { source: number; target: number; similarity: number }[] = [];

  // Calculate all pairwise similarities
  for (let i = 0; i < members.length; i++) {
    for (let j = i + 1; j < members.length; j++) {
      const similarity = calculateMemberSimilarity(members[i].scores, members[j].scores);
      allEdges.push({ source: i, target: j, similarity });
    }
  }

  // Sort by similarity descending
  allEdges.sort((a, b) => b.similarity - a.similarity);

  // Take top X%
  const topPercentCount = Math.ceil(allEdges.length * (edgeTopPercent / 100));
  const topEdges = allEdges.slice(0, topPercentCount);

  // Further filter: only keep edges with similarity >= threshold
  const filteredEdges = topEdges.filter((edge) => edge.similarity >= similarityThreshold);

  // Assign tiers: top 50% of filtered = tier 1 (thick), rest = tier 2 (thin)
  const tier1Count = Math.ceil(filteredEdges.length * 0.5);

  return filteredEdges.map(
    (edge, index): NetworkEdge => ({
      ...edge,
      tier: index < tier1Count ? 1 : 2,
    })
  );
}

/**
 * Check if a member has uniform scores (CV < 0.15)
 * These members should be placed near the center
 */
function hasUniformScores(scores: { A: number; B: number; C: number; D: number }): boolean {
  const values = [scores.A, scores.B, scores.C, scores.D];
  const total = values.reduce((sum, v) => sum + v, 0);
  if (total === 0) return true;

  const mean = total / 4;
  const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / 4;
  const stdDev = Math.sqrt(variance);
  const cv = mean > 0 ? stdDev / mean : 0;

  return cv < 0.15;
}

/**
 * Simple force-directed layout algorithm
 * Places nodes to minimize edge crossings and overlap
 * Requirement 37: All nodes fit within page
 * Requirement 38: Uniform score members placed near center
 */
function calculateForceLayout(
  nodeCount: number,
  edges: NetworkEdge[],
  width: number,
  height: number,
  uniformIndices: number[], // Indices of members with uniform scores
  iterations: number = 200 // Increased iterations for better convergence
): { x: number; y: number }[] {
  // Initialize positions randomly in a circle
  const positions: { x: number; y: number }[] = [];
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) * 0.4; // Larger initial radius

  for (let i = 0; i < nodeCount; i++) {
    // Uniform score members start near center
    if (uniformIndices.includes(i)) {
      // Small random offset from center
      const offsetX = (Math.random() - 0.5) * 60;
      const offsetY = (Math.random() - 0.5) * 60;
      positions.push({
        x: centerX + offsetX,
        y: centerY + offsetY,
      });
    } else {
      const angle = (2 * Math.PI * i) / nodeCount;
      positions.push({
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      });
    }
  }

  // Force-directed simulation
  const repulsionStrength = 5000; // Increased significantly for more spacing
  const attractionStrength = 0.01; // Reduced to prevent nodes from clustering too tightly
  const damping = 0.85;
  const centerGravity = 0.008; // Reduced center gravity
  const uniformCenterGravity = 0.03; // Reduced for uniform members too
  const minDistance = 50; // Minimum distance between nodes

  const velocities = positions.map(() => ({ x: 0, y: 0 }));

  for (let iter = 0; iter < iterations; iter++) {
    // Calculate forces
    const forces = positions.map(() => ({ x: 0, y: 0 }));

    // Repulsion between all nodes
    for (let i = 0; i < nodeCount; i++) {
      for (let j = i + 1; j < nodeCount; j++) {
        const dx = positions[j].x - positions[i].x;
        const dy = positions[j].y - positions[i].y;
        let dist = Math.sqrt(dx * dx + dy * dy) || 1;

        // Enforce minimum distance - stronger repulsion when too close
        if (dist < minDistance) {
          dist = minDistance * 0.5; // Amplify repulsion for close nodes
        }

        const force = repulsionStrength / (dist * dist);
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;

        forces[i].x -= fx;
        forces[i].y -= fy;
        forces[j].x += fx;
        forces[j].y += fy;
      }
    }

    // Attraction along edges (only for connected nodes)
    for (const edge of edges) {
      const dx = positions[edge.target].x - positions[edge.source].x;
      const dy = positions[edge.target].y - positions[edge.source].y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;

      // Only apply attraction if nodes are far apart
      if (dist > minDistance * 1.5) {
        const force = (dist - minDistance) * attractionStrength * edge.similarity;
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;

        forces[edge.source].x += fx;
        forces[edge.source].y += fy;
        forces[edge.target].x -= fx;
        forces[edge.target].y -= fy;
      }
    }

    // Center gravity (stronger for uniform score members)
    for (let i = 0; i < nodeCount; i++) {
      const gravity = uniformIndices.includes(i) ? uniformCenterGravity : centerGravity;
      forces[i].x += (centerX - positions[i].x) * gravity;
      forces[i].y += (centerY - positions[i].y) * gravity;
    }

    // Update velocities and positions
    for (let i = 0; i < nodeCount; i++) {
      velocities[i].x = (velocities[i].x + forces[i].x) * damping;
      velocities[i].y = (velocities[i].y + forces[i].y) * damping;

      positions[i].x += velocities[i].x;
      positions[i].y += velocities[i].y;

      // Keep within bounds with padding
      const padding = 35;
      positions[i].x = Math.max(padding, Math.min(width - padding, positions[i].x));
      positions[i].y = Math.max(padding, Math.min(height - padding, positions[i].y));
    }
  }

  return positions;
}

/**
 * Calculate distance from a point to a line segment
 * Used to detect if a node is too close to an edge
 */
function pointToLineDistance(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): { distance: number; nearestX: number; nearestY: number } {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    // Line segment is a point
    const dist = Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
    return { distance: dist, nearestX: x1, nearestY: y1 };
  }

  // Project point onto line, clamped to segment
  let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  const nearestX = x1 + t * dx;
  const nearestY = y1 + t * dy;
  const distance = Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);

  return { distance, nearestX, nearestY };
}

/**
 * Adjust positions to avoid node-edge overlaps
 * Moves nodes away from edges they are too close to
 */
function avoidEdgeOverlaps(
  positions: { x: number; y: number }[],
  edges: NetworkEdge[],
  nodeRadius: number,
  width: number,
  height: number,
  iterations: number = 50
): { x: number; y: number }[] {
  const result = positions.map((p) => ({ ...p }));
  const minDistanceFromEdge = nodeRadius + 15; // Node radius + buffer
  const paddingTop = 45;
  const paddingBottom = 65;
  const paddingSide = 45;

  for (let iter = 0; iter < iterations; iter++) {
    let moved = false;

    for (let nodeIdx = 0; nodeIdx < result.length; nodeIdx++) {
      const node = result[nodeIdx];

      for (const edge of edges) {
        // Skip if this node is part of the edge
        if (edge.source === nodeIdx || edge.target === nodeIdx) continue;

        const source = result[edge.source];
        const target = result[edge.target];

        const { distance, nearestX, nearestY } = pointToLineDistance(
          node.x,
          node.y,
          source.x,
          source.y,
          target.x,
          target.y
        );

        // If node is too close to edge, push it away
        if (distance < minDistanceFromEdge && distance > 0) {
          const pushStrength = (minDistanceFromEdge - distance) * 0.5;
          const dx = node.x - nearestX;
          const dy = node.y - nearestY;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;

          node.x += (dx / dist) * pushStrength;
          node.y += (dy / dist) * pushStrength;

          // Keep within bounds (asymmetric padding)
          node.x = Math.max(paddingSide, Math.min(width - paddingSide, node.x));
          node.y = Math.max(paddingTop, Math.min(height - paddingBottom, node.y));

          moved = true;
        }
      }
    }

    // If no nodes moved, we're done
    if (!moved) break;
  }

  return result;
}

/**
 * Check if two line segments intersect
 * Returns true if segments (p1-p2) and (p3-p4) intersect
 */
function segmentsIntersect(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  p3x: number,
  p3y: number,
  p4x: number,
  p4y: number
): boolean {
  // Check if segments share an endpoint (not a real crossing)
  const eps = 0.001;
  if (
    (Math.abs(p1x - p3x) < eps && Math.abs(p1y - p3y) < eps) ||
    (Math.abs(p1x - p4x) < eps && Math.abs(p1y - p4y) < eps) ||
    (Math.abs(p2x - p3x) < eps && Math.abs(p2y - p3y) < eps) ||
    (Math.abs(p2x - p4x) < eps && Math.abs(p2y - p4y) < eps)
  ) {
    return false;
  }

  const d1x = p2x - p1x;
  const d1y = p2y - p1y;
  const d2x = p4x - p3x;
  const d2y = p4y - p3y;

  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < eps) return false; // Parallel

  const dx = p3x - p1x;
  const dy = p3y - p1y;

  const t = (dx * d2y - dy * d2x) / cross;
  const u = (dx * d1y - dy * d1x) / cross;

  // Check if intersection point is within both segments (excluding endpoints)
  return t > 0.01 && t < 0.99 && u > 0.01 && u < 0.99;
}

/**
 * Reduce edge crossings by moving nodes
 * Detects crossing edges and moves their endpoints to reduce crossings
 */
function reduceEdgeCrossings(
  positions: { x: number; y: number }[],
  edges: NetworkEdge[],
  width: number,
  height: number,
  iterations: number = 50
): { x: number; y: number }[] {
  const result = positions.map((p) => ({ ...p }));
  const paddingTop = 45;
  const paddingBottom = 65;
  const paddingSide = 45;

  for (let iter = 0; iter < iterations; iter++) {
    let improved = false;

    // Check all pairs of edges for crossings
    for (let i = 0; i < edges.length; i++) {
      for (let j = i + 1; j < edges.length; j++) {
        const e1 = edges[i];
        const e2 = edges[j];

        // Skip if edges share a node
        if (
          e1.source === e2.source ||
          e1.source === e2.target ||
          e1.target === e2.source ||
          e1.target === e2.target
        ) {
          continue;
        }

        const p1 = result[e1.source];
        const p2 = result[e1.target];
        const p3 = result[e2.source];
        const p4 = result[e2.target];

        if (segmentsIntersect(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y, p4.x, p4.y)) {
          improved = true;

          // Try to resolve by moving one of the nodes perpendicular to its edge
          // Move the node that's closest to the intersection
          const moveAmount = 15;

          // Calculate perpendicular direction for edge 1
          const dx1 = p2.x - p1.x;
          const dy1 = p2.y - p1.y;
          const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1) || 1;
          const perpX1 = -dy1 / len1;
          const perpY1 = dx1 / len1;

          // Move source of edge 1 perpendicular
          const newX = p1.x + perpX1 * moveAmount;
          const newY = p1.y + perpY1 * moveAmount;

          // Check if new position is within bounds
          if (
            newX >= paddingSide &&
            newX <= width - paddingSide &&
            newY >= paddingTop &&
            newY <= height - paddingBottom
          ) {
            result[e1.source].x = newX;
            result[e1.source].y = newY;
          } else {
            // Try opposite direction
            const altX = p1.x - perpX1 * moveAmount;
            const altY = p1.y - perpY1 * moveAmount;
            if (
              altX >= paddingSide &&
              altX <= width - paddingSide &&
              altY >= paddingTop &&
              altY <= height - paddingBottom
            ) {
              result[e1.source].x = altX;
              result[e1.source].y = altY;
            }
          }
        }
      }
    }

    if (!improved) break;
  }

  return result;
}

/**
 * Separate overlapping nodes
 * Ensures all nodes have minimum distance from each other
 */
function separateOverlappingNodes(
  positions: { x: number; y: number }[],
  minDistance: number,
  width: number,
  height: number,
  iterations: number = 100
): { x: number; y: number }[] {
  const result = positions.map((p) => ({ ...p }));
  const paddingTop = 45;
  const paddingBottom = 65; // More padding for labels
  const paddingSide = 45;

  for (let iter = 0; iter < iterations; iter++) {
    let moved = false;

    for (let i = 0; i < result.length; i++) {
      for (let j = i + 1; j < result.length; j++) {
        const dx = result[j].x - result[i].x;
        const dy = result[j].y - result[i].y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // If nodes are too close, push them apart
        if (dist < minDistance) {
          moved = true;
          const overlap = minDistance - dist;
          const pushDistance = overlap / 2 + 1;

          // Calculate push direction (if same position, use random direction)
          let pushX: number, pushY: number;
          if (dist < 0.1) {
            // Nodes at same position - push in random direction
            const angle = Math.random() * 2 * Math.PI;
            pushX = Math.cos(angle) * pushDistance;
            pushY = Math.sin(angle) * pushDistance;
          } else {
            pushX = (dx / dist) * pushDistance;
            pushY = (dy / dist) * pushDistance;
          }

          // Push nodes apart
          result[i].x -= pushX;
          result[i].y -= pushY;
          result[j].x += pushX;
          result[j].y += pushY;

          // Keep within bounds
          result[i].x = Math.max(paddingSide, Math.min(width - paddingSide, result[i].x));
          result[i].y = Math.max(paddingTop, Math.min(height - paddingBottom, result[i].y));
          result[j].x = Math.max(paddingSide, Math.min(width - paddingSide, result[j].x));
          result[j].y = Math.max(paddingTop, Math.min(height - paddingBottom, result[j].y));
        }
      }
    }

    if (!moved) break;
  }

  return result;
}

/**
 * Pull out-of-bounds nodes back into the visible area
 * Simply moves nodes that are outside the boundary back inside
 * Edge lengths will change but that's acceptable since distance has no meaning
 */
function pullNodesIntoBounds(
  positions: { x: number; y: number }[],
  width: number,
  height: number,
  nodeRadius: number
): { x: number; y: number }[] {
  const result = positions.map((p) => ({ ...p }));

  // Strict bounds - account for node radius and label below
  const minX = nodeRadius + 20;
  const maxX = width - nodeRadius - 20;
  const minY = nodeRadius + 20;
  const maxY = height - nodeRadius - 30; // Extra space for label below node

  for (let i = 0; i < result.length; i++) {
    // Simply clamp to bounds
    if (result[i].x < minX) result[i].x = minX;
    if (result[i].x > maxX) result[i].x = maxX;
    if (result[i].y < minY) result[i].y = minY;
    if (result[i].y > maxY) result[i].y = maxY;
  }

  return result;
}

/**
 * Final adjustment: ensure all nodes are visible and not overlapping
 * This is the last step after all other layout adjustments
 */
function finalAdjustment(
  positions: { x: number; y: number }[],
  width: number,
  height: number,
  nodeRadius: number,
  minDistance: number
): { x: number; y: number }[] {
  const result = positions.map((p) => ({ ...p }));

  // Strict bounds - generous margins
  const minX = nodeRadius + 25;
  const maxX = width - nodeRadius - 25;
  const minY = nodeRadius + 25;
  const maxY = height - nodeRadius - 35; // Extra for label

  // First, pull all nodes into bounds
  for (let i = 0; i < result.length; i++) {
    result[i].x = Math.max(minX, Math.min(maxX, result[i].x));
    result[i].y = Math.max(minY, Math.min(maxY, result[i].y));
  }

  // Then separate any overlapping nodes (multiple passes)
  for (let pass = 0; pass < 100; pass++) {
    let moved = false;

    for (let i = 0; i < result.length; i++) {
      for (let j = i + 1; j < result.length; j++) {
        const dx = result[j].x - result[i].x;
        const dy = result[j].y - result[i].y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < minDistance) {
          moved = true;
          const overlap = minDistance - dist;
          const pushDist = overlap / 2 + 2;

          let pushX: number, pushY: number;
          if (dist < 0.1) {
            // Same position - push in a deterministic direction based on index
            const angle = (i * 137.5 * Math.PI) / 180; // Golden angle for good distribution
            pushX = Math.cos(angle) * pushDist;
            pushY = Math.sin(angle) * pushDist;
          } else {
            pushX = (dx / dist) * pushDist;
            pushY = (dy / dist) * pushDist;
          }

          result[i].x -= pushX;
          result[i].y -= pushY;
          result[j].x += pushX;
          result[j].y += pushY;

          // Keep within bounds after push
          result[i].x = Math.max(minX, Math.min(maxX, result[i].x));
          result[i].y = Math.max(minY, Math.min(maxY, result[i].y));
          result[j].x = Math.max(minX, Math.min(maxX, result[j].x));
          result[j].y = Math.max(minY, Math.min(maxY, result[j].y));
        }
      }
    }

    if (!moved) break;
  }

  return result;
}

/**
 * Improved edge crossing reduction
 * Detects crossing edges and moves nodes to eliminate crossings
 */
function improvedEdgeCrossingReduction(
  positions: { x: number; y: number }[],
  edges: NetworkEdge[],
  width: number,
  height: number,
  nodeRadius: number,
  iterations: number = 100
): { x: number; y: number }[] {
  const result = positions.map((p) => ({ ...p }));

  const minX = nodeRadius + 25;
  const maxX = width - nodeRadius - 25;
  const minY = nodeRadius + 25;
  const maxY = height - nodeRadius - 35;

  for (let iter = 0; iter < iterations; iter++) {
    let improved = false;

    // Check all pairs of edges for crossings
    for (let i = 0; i < edges.length; i++) {
      for (let j = i + 1; j < edges.length; j++) {
        const e1 = edges[i];
        const e2 = edges[j];

        // Skip if edges share a node
        if (
          e1.source === e2.source ||
          e1.source === e2.target ||
          e1.target === e2.source ||
          e1.target === e2.target
        ) {
          continue;
        }

        const p1 = result[e1.source];
        const p2 = result[e1.target];
        const p3 = result[e2.source];
        const p4 = result[e2.target];

        if (segmentsIntersect(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y, p4.x, p4.y)) {
          improved = true;

          // Try multiple strategies to resolve the crossing
          const moveAmount = 20;

          // Strategy 1: Move one endpoint of edge 1 perpendicular
          const dx1 = p2.x - p1.x;
          const dy1 = p2.y - p1.y;
          const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1) || 1;
          const perpX1 = -dy1 / len1;
          const perpY1 = dx1 / len1;

          // Try moving e1.source
          let newX = p1.x + perpX1 * moveAmount;
          let newY = p1.y + perpY1 * moveAmount;

          if (newX >= minX && newX <= maxX && newY >= minY && newY <= maxY) {
            result[e1.source].x = newX;
            result[e1.source].y = newY;
          } else {
            // Try opposite direction
            newX = p1.x - perpX1 * moveAmount;
            newY = p1.y - perpY1 * moveAmount;
            if (newX >= minX && newX <= maxX && newY >= minY && newY <= maxY) {
              result[e1.source].x = newX;
              result[e1.source].y = newY;
            } else {
              // Try moving e2.source instead
              const dx2 = p4.x - p3.x;
              const dy2 = p4.y - p3.y;
              const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2) || 1;
              const perpX2 = -dy2 / len2;
              const perpY2 = dx2 / len2;

              newX = p3.x + perpX2 * moveAmount;
              newY = p3.y + perpY2 * moveAmount;
              if (newX >= minX && newX <= maxX && newY >= minY && newY <= maxY) {
                result[e2.source].x = newX;
                result[e2.source].y = newY;
              }
            }
          }
        }
      }
    }

    if (!improved) break;
  }

  return result;
}

/**
 * Fit all positions within bounds by compressing the layout
 * Keeps node sizes unchanged, only adjusts positions
 */
function fitPositionsWithinBounds(
  positions: { x: number; y: number }[],
  width: number,
  height: number,
  nodeRadius: number
): { x: number; y: number }[] {
  if (positions.length === 0) return positions;

  // Account for node size and label - more padding at bottom for labels
  const paddingTop = nodeRadius + 25;
  const paddingBottom = nodeRadius + 50; // Extra space for name labels
  const paddingSide = nodeRadius + 30;

  // Find current bounds (including label space)
  let minX = Infinity,
    maxX = -Infinity;
  let minY = Infinity,
    maxY = -Infinity;

  for (const pos of positions) {
    minX = Math.min(minX, pos.x);
    maxX = Math.max(maxX, pos.x);
    minY = Math.min(minY, pos.y);
    maxY = Math.max(maxY, pos.y);
  }

  const currentWidth = maxX - minX;
  const currentHeight = maxY - minY;

  // Available space (asymmetric for top/bottom)
  const availableWidth = width - paddingSide * 2;
  const availableHeight = height - paddingTop - paddingBottom;

  // Calculate scale factors (only compress, never expand)
  const scaleX = currentWidth > availableWidth ? availableWidth / currentWidth : 1;
  const scaleY = currentHeight > availableHeight ? availableHeight / currentHeight : 1;
  const scale = Math.min(scaleX, scaleY);

  // Center of current positions
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  // Target center (slightly higher to account for bottom labels)
  const targetCenterX = width / 2;
  const targetCenterY = paddingTop + availableHeight / 2;

  // Transform positions: scale around center, then translate to target center
  return positions.map((pos) => {
    // Scale relative to current center
    const scaledX = centerX + (pos.x - centerX) * scale;
    const scaledY = centerY + (pos.y - centerY) * scale;

    // Translate to target center
    const finalX = scaledX - centerX + targetCenterX;
    const finalY = scaledY - centerY + targetCenterY;

    // Ensure within bounds (asymmetric)
    return {
      x: Math.max(paddingSide, Math.min(width - paddingSide, finalX)),
      y: Math.max(paddingTop, Math.min(height - paddingBottom, finalY)),
    };
  });
}

/**
 * Generate network graph data with layout
 * @param members Array of member scores
 * @param width Graph width
 * @param height Graph height
 * @param options Network graph options (edgeTopPercent, similarityThreshold)
 */
export function generateNetworkGraph(
  members: MemberScore[],
  width: number = 600,
  height: number = 550,
  options: NetworkGraphOptions = {},
  nodeRadius: number = 12 // 人数が多いときは呼び出し側で縮小した値を渡す。generateNetworkSvg と一致させること
): NetworkGraphData {
  const { edgeTopPercent = 10, similarityThreshold = 0.7 } = options;
  // ノード間の最小距離。基本は 50px だが、人数が多く 50px では収まらない場合は
  // 描画領域の面積から収まる間隔まで自動的に詰める(重なり・見切れを防止)。
  // 少人数(既定の 50px で収まる)のときは従来どおり 50px。
  const idealDistance = Math.sqrt((width * height) / Math.max(1, members.length)) * 0.85;
  const minNodeDistance = Math.max(2 * nodeRadius + 6, Math.min(50, idealDistance));

  // Calculate edges with options
  const edges = calculateNetworkEdges(members, edgeTopPercent, similarityThreshold);

  // Find members with uniform scores (Requirement 38)
  const uniformIndices: number[] = [];
  members.forEach((member, index) => {
    if (hasUniformScores(member.scores)) {
      uniformIndices.push(index);
    }
  });

  // Calculate initial layout with uniform indices
  let positions = calculateForceLayout(members.length, edges, width, height, uniformIndices);

  // Step 1: Pull nodes into bounds first
  positions = pullNodesIntoBounds(positions, width, height, nodeRadius);

  // Step 2: Separate overlapping nodes
  positions = separateOverlappingNodes(positions, minNodeDistance, width, height);

  // Step 3: Improved edge crossing reduction
  positions = improvedEdgeCrossingReduction(positions, edges, width, height, nodeRadius);

  // Step 4: Avoid node-edge overlaps
  positions = avoidEdgeOverlaps(positions, edges, nodeRadius, width, height);

  // Step 5: Final adjustment - ensure all nodes visible and separated
  positions = finalAdjustment(positions, width, height, nodeRadius, minNodeDistance);

  // Step 6: One more edge crossing pass after final adjustment
  positions = improvedEdgeCrossingReduction(positions, edges, width, height, nodeRadius, 50);

  // Step 7: Final bounds check
  positions = pullNodesIntoBounds(positions, width, height, nodeRadius);

  // Create nodes
  const nodes: NetworkNode[] = members.map((member, index) => ({
    id: index,
    name: member.name,
    scores: member.scores,
    x: positions[index].x,
    y: positions[index].y,
  }));

  return { nodes, edges };
}

/**
 * Calculate radar chart path for mini radar in network graph
 */
function calculateMiniRadarPath(
  scores: { A: number; B: number; C: number; D: number },
  centerX: number,
  centerY: number,
  maxRadius: number
): string {
  const maxVal = 12;

  const getPoint = (angle: number, value: number) => {
    const rad = (angle * Math.PI) / 180;
    const r = (value / maxVal) * maxRadius;
    return {
      x: centerX + r * Math.cos(rad),
      y: centerY - r * Math.sin(rad),
    };
  };

  // A=135°, B=225°, C=315°, D=45°
  const pA = getPoint(135, scores.A);
  const pB = getPoint(225, scores.B);
  const pC = getPoint(315, scores.C);
  const pD = getPoint(45, scores.D);

  return `M${pA.x} ${pA.y} L${pB.x} ${pB.y} L${pC.x} ${pC.y} L${pD.x} ${pD.y} Z`;
}

/**
 * Generate SVG string for network graph
 */
export function generateNetworkSvg(
  graphData: NetworkGraphData,
  width: number = 600,
  height: number = 550,
  nodeRadius: number = 12 // generateNetworkGraph に渡した値と一致させること
): string {
  const { nodes, edges } = graphData;

  // Generate edge lines
  const edgeLines = edges
    .map((edge) => {
      const source = nodes[edge.source];
      const target = nodes[edge.target];
      const strokeWidth = edge.tier === 1 ? 2.5 : 1;
      const opacity = edge.tier === 1 ? 0.7 : 0.4;

      return `<line 
      x1="${source.x}" y1="${source.y}" 
      x2="${target.x}" y2="${target.y}" 
      stroke="#6B7280" 
      stroke-width="${strokeWidth}" 
      opacity="${opacity}"
    />`;
    })
    .join('\n');

  // Generate node groups (radar charts with names)
  const nodeGroups = nodes
    .map((node) => {
      const radarPath = calculateMiniRadarPath(node.scores, node.x, node.y, nodeRadius);

      // Background circle
      const bgCircle = `<circle cx="${node.x}" cy="${node.y}" r="${nodeRadius + 1}" fill="white" stroke="#E5E7EB" stroke-width="0.5"/>`;

      // Radar chart background (grid)
      const gridCircle = `<circle cx="${node.x}" cy="${node.y}" r="${nodeRadius}" fill="#F9FAFB" stroke="#E5E7EB" stroke-width="0.3"/>`;

      // Radar chart path
      const radarFill = `<path d="${radarPath}" fill="rgba(59, 130, 246, 0.3)" stroke="#3B82F6" stroke-width="1"/>`;

      // Name label (below the node) - font size 8px
      const nameLabel = `<text x="${node.x}" y="${node.y + nodeRadius + 10}" text-anchor="middle" font-size="8" fill="#374151">${escapeXml(node.name)}</text>`;

      return `<g class="network-node">
      ${bgCircle}
      ${gridCircle}
      ${radarFill}
      ${nameLabel}
    </g>`;
    })
    .join('\n');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" style="display:block;width:${width}px;height:${height}px;">
    <defs>
      <style>
        .network-node text { font-family: sans-serif; }
      </style>
    </defs>
    <rect width="${width}" height="${height}" fill="transparent"/>
    <g class="edges">
      ${edgeLines}
    </g>
    <g class="nodes">
      ${nodeGroups}
    </g>
  </svg>`;
}

/**
 * Escape XML special characters
 */
function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Generate complete network graph as data URL for embedding in HTML
 */
export function generateNetworkGraphDataUrl(
  members: MemberScore[],
  width: number = 600,
  height: number = 550,
  options: NetworkGraphOptions = {}
): string {
  const graphData = generateNetworkGraph(members, width, height, options);
  const svg = generateNetworkSvg(graphData, width, height);

  // Convert to data URL
  const encoded = encodeURIComponent(svg);
  return `data:image/svg+xml,${encoded}`;
}
